/* ============================================================
   eShopBoost — PDP Delivery Timeline (Phase D, v1)
   ------------------------------------------------------------
   <eshopboost-delivery> custom element. Reads config from an
   embedded JSON script tag, computes business-day delivery
   estimates anchored to the store timezone, and fills the
   sentence, optional cutoff countdown line, and timeline
   sublabels rendered by snippets/eshopboost-delivery-timeline.liquid.
   Without JS the Liquid fallback (day ranges, no dates) stays.
   Display only — real shipping is configured in Shopify Admin.
   ============================================================ */
(function () {
  'use strict';

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 86400000;

  class EshopboostDelivery extends HTMLElement {
    connectedCallback() {
      var cfgEl = this.querySelector('[data-pdl-config]');
      if (!cfgEl) return;
      try {
        this.cfg = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }
      this.renderAll();
      var self = this;
      this.pdlTimer = setInterval(function () { self.renderAll(); }, 60000);
    }

    disconnectedCallback() {
      if (this.pdlTimer) clearInterval(this.pdlTimer);
    }

    /* ---- pure date helpers (UTC-anchored virtual dates) ---- */

    static isBusinessDay(ms) {
      var dow = new Date(ms).getUTCDay();
      return dow >= 1 && dow <= 5;
    }

    static rollToBusinessDay(ms) {
      while (!EshopboostDelivery.isBusinessDay(ms)) ms += DAY_MS;
      return ms;
    }

    static addBusinessDays(ms, n) {
      while (n > 0) {
        ms += DAY_MS;
        if (EshopboostDelivery.isBusinessDay(ms)) n -= 1;
      }
      return ms;
    }

    static fmtShort(ms) {
      var d = new Date(ms);
      return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
    }

    static fmtShortRange(fromMs, toMs) {
      if (fromMs === toMs) return EshopboostDelivery.fmtShort(fromMs);
      var a = new Date(fromMs);
      var b = new Date(toMs);
      if (a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear()) {
        return MONTHS[a.getUTCMonth()] + ' ' + a.getUTCDate() + ' - ' + b.getUTCDate();
      }
      return EshopboostDelivery.fmtShort(fromMs) + ' - ' + EshopboostDelivery.fmtShort(toMs);
    }

    static fmtSentence(ms, style) {
      var d = new Date(ms);
      var y = d.getUTCFullYear();
      var m = d.getUTCMonth() + 1;
      var day = d.getUTCDate();
      var mm = (m < 10 ? '0' : '') + m;
      var dd = (day < 10 ? '0' : '') + day;
      if (style === 'dmy') return dd + '/' + mm + '/' + y;
      if (style === 'written') return MONTHS[m - 1] + ' ' + day + ', ' + y;
      return mm + '/' + dd + '/' + y; /* mdy default */
    }

    static parseCutoff(str) {
      var m = /^(\d{1,2}):(\d{2})$/.exec((str || '').trim());
      if (!m) return null;
      var hh = parseInt(m[1], 10);
      var mn = parseInt(m[2], 10);
      if (hh > 23 || mn > 59) return null;
      return hh * 60 + mn;
    }

    static escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /* Current date/time in the configured timezone. Falls back to
       the visitor's local clock if the timezone name is invalid. */
    static nowInTimeZone(tz) {
      var now = new Date();
      var parts;
      try {
        parts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        }).formatToParts(now);
      } catch (e) {
        return {
          dayMs: Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
          minutes: now.getHours() * 60 + now.getMinutes()
        };
      }
      var v = {};
      for (var i = 0; i < parts.length; i++) v[parts[i].type] = parts[i].value;
      return {
        dayMs: Date.UTC(parseInt(v.year, 10), parseInt(v.month, 10) - 1, parseInt(v.day, 10)),
        minutes: parseInt(v.hour, 10) * 60 + parseInt(v.minute, 10)
      };
    }

    /* ---- main computation ---- */

    static compute(cfg, now) {
      var procMin = Math.min(cfg.procMin, cfg.procMax);
      var procMax = Math.max(cfg.procMin, cfg.procMax);
      var shipMin = Math.min(cfg.shipMin, cfg.shipMax);
      var shipMax = Math.max(cfg.shipMin, cfg.shipMax);
      var cutoffMins = cfg.cutoffEnable ? EshopboostDelivery.parseCutoff(cfg.cutoffTime) : null;

      var start = now.dayMs;
      var countdownMins = null;
      if (cutoffMins !== null && EshopboostDelivery.isBusinessDay(start)) {
        if (now.minutes >= cutoffMins) {
          start += DAY_MS;
        } else {
          countdownMins = cutoffMins - now.minutes;
        }
      }
      start = EshopboostDelivery.rollToBusinessDay(start);

      return {
        purchased: now.dayMs,
        procFrom: start,
        procTo: EshopboostDelivery.addBusinessDays(start, procMax),
        deliverFrom: EshopboostDelivery.addBusinessDays(start, procMin + shipMin),
        deliverTo: EshopboostDelivery.addBusinessDays(start, procMax + shipMax),
        countdownMins: countdownMins
      };
    }

    renderAll() {
      var cfg = this.cfg;
      var now = EshopboostDelivery.nowInTimeZone(cfg.tz);
      var r = EshopboostDelivery.compute(cfg, now);

      var sentenceEl = this.querySelector('[data-pdl-sentence]');
      if (sentenceEl && cfg.template) {
        var from = '<strong>' + EshopboostDelivery.fmtSentence(r.deliverFrom, cfg.dateFormat) + '</strong>';
        var to = '<strong>' + EshopboostDelivery.fmtSentence(r.deliverTo, cfg.dateFormat) + '</strong>';
        sentenceEl.innerHTML = EshopboostDelivery.escapeHtml(cfg.template)
          .split('[date_from]').join(from)
          .split('[date_to]').join(to);
      }

      var cutoffEl = this.querySelector('[data-pdl-cutoff]');
      if (cutoffEl) {
        if (r.countdownMins !== null && cfg.cutoffLine) {
          var h = Math.floor(r.countdownMins / 60);
          var mn = r.countdownMins % 60;
          var cd = h > 0 ? h + 'h ' + mn + 'm' : mn + 'm';
          cutoffEl.innerHTML = EshopboostDelivery.escapeHtml(cfg.cutoffLine)
            .split('[cutoff_countdown]').join('<strong>' + cd + '</strong>');
          cutoffEl.hidden = false;
        } else {
          cutoffEl.hidden = true;
        }
      }

      var sub1 = this.querySelector('[data-pdl-sub="1"]');
      var sub2 = this.querySelector('[data-pdl-sub="2"]');
      var sub3 = this.querySelector('[data-pdl-sub="3"]');
      if (sub1) sub1.textContent = EshopboostDelivery.fmtShort(r.purchased);
      if (sub2) sub2.textContent = EshopboostDelivery.fmtShortRange(r.procFrom, r.procTo);
      if (sub3) sub3.textContent = EshopboostDelivery.fmtShortRange(r.deliverFrom, r.deliverTo);
    }
  }

  if (!customElements.get('eshopboost-delivery')) {
    customElements.define('eshopboost-delivery', EshopboostDelivery);
  }
})();